import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { HealthBadge } from '../health/index.js';
import { ProgressBar } from './ProjectTable.js';
import { getStatusVariant } from './ChildTicketsModal.js';
import { MemberRoleBadges } from './MemberRoleBadges.js';
import type { MemberProject } from '../../../types/project.js';

/**
 * メンバー担当プロジェクト一覧テーブル
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business メンバーが開発系子チケットを担当している、または自身が開発リーダーでかつ母票のアサイン決定日(custom field 213634)が設定済みであるGen2親プロジェクトの一覧テーブル。案件キー・案件名・ロール（担当子チケット種別が開発なら開発者、レビュー派生ならレビュアー、自身が開発リーダーなら開発リーダー。複数該当なら複数バッジ）・担当チーム・主担当・進捗率・メンバー実績/予定pt・健康度・ステータスを表示
 * @graph-connects MemberRoleBadges [calls] 担当ロールバッジ描画
 */
export function MemberProjectsTable({ projects }: { projects: MemberProject[] }) {
  const { t } = useTranslation();

  if (projects.length === 0) {
    return (
      <p className="text-center py-8 text-[var(--color-text-muted)]">
        {t('members.detail.noProjects')}
      </p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full table-fixed">
        <colgroup>
          <col className="w-[120px]" />
          <col className="w-[240px]" />
          <col className="w-[130px]" />
          <col className="w-[100px]" />
          <col className="w-[90px]" />
          <col className="w-[160px]" />
          <col className="w-[100px]" />
          <col className="w-[70px]" />
          <col className="w-[90px]" />
        </colgroup>
        <thead>
          <tr className="border-b border-[var(--color-border)]">
            <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)] whitespace-nowrap">
              {t('projects.fields.issueKey')}
            </th>
            <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)] whitespace-nowrap">
              {t('projects.fields.summary')}
            </th>
            <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)] whitespace-nowrap">
              {t('members.detail.role')}
            </th>
            <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)] whitespace-nowrap">
              {t('projects.fields.responsibleTeam')}
            </th>
            <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)] whitespace-nowrap">
              {t('projects.fields.pl')}
            </th>
            <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)] whitespace-nowrap">
              {t('projects.fields.completionRatio')}
            </th>
            <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)] whitespace-nowrap">
              {t('members.detail.memberPoints')}
            </th>
            <th className="text-center py-3 px-4 text-sm font-medium text-[var(--color-text-muted)] whitespace-nowrap">
              {t('projects.health.title')}
            </th>
            <th
              data-align-anchor="status"
              className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)] whitespace-nowrap"
            >
              {t('projects.fields.status')}
            </th>
          </tr>
        </thead>
        <tbody>
          {projects.map((project) => (
            <tr
              key={project.id}
              className="border-b border-[var(--color-border)] last:border-b-0 hover:bg-[var(--color-bg-hover)] transition-colors"
            >
              <td className="py-3 px-4 text-sm text-[var(--color-accent-primary)] whitespace-nowrap">
                <Link
                  to="/projects/$pjKey"
                  params={{ pjKey: project.issueKey }}
                  className="hover:underline"
                >
                  {project.issueKey}
                </Link>
              </td>
              <td
                className="py-3 px-4 text-sm text-[var(--color-text-primary)] truncate"
                title={project.summary}
              >
                {project.summary}
              </td>
              <td className="py-3 px-4 text-sm">
                <MemberRoleBadges
                  role={{
                    isDeveloper: project.memberIsDeveloper,
                    isReviewer: project.memberIsReviewer,
                    isDevLeader: project.memberIsDevLeader,
                  }}
                />
              </td>
              <td className="py-3 px-4 text-sm text-[var(--color-text-secondary)] truncate">
                {project.responsibleTeam ?? '-'}
              </td>
              <td className="py-3 px-4 text-sm text-[var(--color-text-secondary)] truncate">
                {project.assignee ?? '-'}
              </td>
              <td className="py-3 px-4 text-sm">
                <ProgressBar ratio={project.completionRatio} />
              </td>
              <td className="py-3 px-4 text-sm text-[var(--color-text-secondary)] whitespace-nowrap">
                {project.memberActualPoint}/{project.totalPlannedPoint}
              </td>
              <td className="py-3 px-4 text-sm text-center">
                <HealthBadge health={project.health} />
              </td>
              <td className="py-3 px-4 text-sm">
                <Badge variant={getStatusVariant(project.status)} size="sm">
                  {project.status}
                </Badge>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
